import { useEffect, useEffectEvent, useState } from 'react';

import { DEBUG_I18N, REVISION } from '#src/config.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import type { AvailableLanguage } from '#src/lib/i18n.ts';

let DEV_TRANSLATIONS: Record<string, { default: Record<string, string> }> = {};
if (import.meta.env.DEV) {
  // To get the translations run the `yarn i18n:lokalise:download` script
  DEV_TRANSLATIONS = import.meta.glob('./translations/*.json', { eager: true });
}

export const useTranslations = (
  locale: AvailableLanguage,
  testMode = false
) => {
  const formattedLocaleFromProps = locale.split('-')[0];
  const [formattedLocale, setFormattedLocale] = useState(
    formattedLocaleFromProps
  );
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState<true | false | 'loading'>(false);

  const loadLiveTranslations = async (revision: string, localeArg: string) => {
    setLoaded('loading');
    const response = await fetch(
      `${FRONTEND_ASSET_HOST}/i18n/${revision}/${localeArg}.json.gz`
    );
    let json: Record<string, string> = await response.json();
    if (DEBUG_I18N) {
      json = Object.fromEntries(
        Object.entries(json).map(([k, v]) => [k, `${v} [${k}]`])
      );
    }
    setTranslations(existingTranslations => ({
      ...existingTranslations,
      ...json,
    }));
    setLoaded(true);
  };

  if (formattedLocale !== formattedLocaleFromProps && loaded) {
    setFormattedLocale(formattedLocaleFromProps);
    setLoaded(false);
  }

  const onLoadTranslations = useEffectEvent((localeArg: string) => {
    if (!loaded) {
      if (!testMode) {
        if (REVISION === 'development') {
          if (localeArg === 'en') {
            // for english translations, just rely on what's in the code
            setLoaded(true);
          } else if (process.env.MASTER_COMMIT_REF) {
            // otherwise load the associated master translations
            loadLiveTranslations(process.env.MASTER_COMMIT_REF, localeArg);
          }
        } else {
          loadLiveTranslations(REVISION, localeArg);
        }
      } else {
        if (localeArg !== 'en') {
          // In test mode access translations locally
          const key = `./translations/${localeArg}.json`;

          if (Object.keys(DEV_TRANSLATIONS).includes(key)) {
            const data = DEV_TRANSLATIONS[key];
            setTranslations(data.default);
          }
        }
        setLoaded(true);
      }
    }
  });

  useEffect(() => {
    onLoadTranslations(formattedLocale);
  }, [formattedLocale]);

  return { loaded: loaded === true, translations };
};
